

























import { computed, defineComponent, onBeforeMount, watch, watchEffect } from '@vue/composition-api';

import store from '@/store';
import { snackbar } from '@/components/GlobalSnackbar';
import { loginExisting, loggedIn } from '@/utils/auth';
import Appbar from '@/components/Appbar/Appbar.vue';
import { actions } from '@/controller';
import Annotator from './Annotator.vue';
import Sidebar from './Sidebar.vue';
import { getShowNotificationsOnStartup } from '@/utils/storage';
import { notificationsMenu } from '@/filemenu/admin/state';
import { changePasswordMenu, userMenu, mustChangePassword } from '@/components/Appbar/state';

const appReadyForDataset = new Promise((resolve) => {
  // Don't use useClient, to make use of reactive state
  const ready = computed(() => (
    store.state.client !== null
    && store.state.application.applicationData !== null
  ));

  watchEffect(() => {
    if (ready.value) { resolve(true); }
  });
});

export default defineComponent({
  name: 'Home',
  components: {
    Appbar,
    Sidebar,
    Annotator,
  },
  setup(props, ctx) {
    const router = ctx.root.$router;
    const user = computed(() => store.state.client?.user);
    const loadedDataset = computed(() => store.state.dataset.dataset);

    function redirectToLogin() {
      router.push('/login');
    }

    // Login Existing User
    onBeforeMount(async () => {
      await loginExisting();
      if (!loggedIn.value) {
        redirectToLogin();
      }

      if (store.getters.isPasswordResetTime) {
        mustChangePassword.value = true;
        userMenu.value = true;
        changePasswordMenu.value = true;
      } else {
        userMenu.value = false;
      }

      // Logged in, show notifications if preference is configured
      const showNotifications = getShowNotificationsOnStartup();
      if (showNotifications && store.getters.isAdmin) {
        notificationsMenu.value = true;
      }
    });

    // Display message on user login/logout
    watch(() => user.value, (newUser, oldUser) => {
      if (newUser) {
        snackbar.dispatch({ text: `Welcome ${newUser.username}`, options: { right: true } });
      } else if (oldUser) {
        snackbar.dispatch({ text: `Goodbye ${oldUser.username}`, options: { right: true } });
        redirectToLogin();
      }
    });

    const currentRoute = computed(() => ctx.root.$route);
    const datasetId = computed(() => store.state.dataset.datasetId);
    const isUserDataset = computed(() => store.state.dataset.userDataset);

    // Set current route based on dataset
    watch(datasetId, (id) => {
      const route = currentRoute.value;
      const { dataset } = route.query;

      // No dataset selected and no query, no change needed.
      if ((datasetId === null && !dataset) || id === dataset) {
        return;
      }

      router.push({
        name: route.name || undefined,
        path: route.path,
        params: route.params,
        query: {
          ...route.query,
          dataset: id || undefined,
          user: isUserDataset.value ? 'true' : undefined,
        },
      });
    });

    // Set current dataset based on route
    watch(currentRoute, async (route) => {
      // The dataset ID
      const dataset = route.query.dataset as string | null | undefined;

      // Whether this dataset is a user dataset or not
      const userDataset = route.query.user as string | null | undefined;

      const emptyRoute = {
        name: route.name || undefined,
        path: route.path,
        params: route.params,
        query: {
          ...route.query,
          dataset: undefined,
          user: undefined,
        },
      };

      if (
        route.path !== '/'
        || Array.isArray(dataset)
        || (dataset && datasetId.value === dataset)
      ) { return; }

      // If nothing to do, clear url and dataset
      if (!dataset) {
        if (dataset !== undefined) {
          router.replace(emptyRoute);
        }

        actions.clearDataset();
        return;
      }

      await appReadyForDataset;
      try {
        await actions.fetchDataset(dataset, !!userDataset);
      } catch (err) {
        actions.clearDataset();
        router.replace(emptyRoute);

        snackbar.dispatch({
          text: 'Invalid Dataset ID',
          options: { color: 'error', top: true },
          buttonIcon: 'mdi-close',
        });
      }
    }, { immediate: true });

    return { loadedDataset, loggedIn };
  },
});
